import { useFormContext } from 'react-hook-form'
import { useTranslation } from 'react-i18next'

import { DatePicker } from '@/components/ui/date-picker'
import {
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form'
import { Input } from '@/components/ui/input'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { AdminSubscriptionPlan } from '@/types/AdminSubscription'

import { AdminAccountSubscriptionFormValues } from './schema'

interface AdminAccountSubscriptionFormFieldsProps {
  plans: AdminSubscriptionPlan[]
  onPlanChange: (planId: string) => void
  onStartDateChange: (startDate: string) => void
}

export const AdminAccountSubscriptionFormFields = ({
  plans,
  onPlanChange,
  onStartDateChange,
}: AdminAccountSubscriptionFormFieldsProps) => {
  const { t } = useTranslation(undefined, {
    keyPrefix: 'admin.accounts.detail.subscriptions.form',
  })

  const { control } = useFormContext<AdminAccountSubscriptionFormValues>()

  return (
    <div className="space-y-4">
      <FormField
        control={control}
        name="subscriptionPlanId"
        render={({ field }) => (
          <FormItem>
            <FormLabel>{t('plan.label')}</FormLabel>
            <Select
              value={field.value}
              onValueChange={(value) => {
                field.onChange(value)
                onPlanChange(value)
              }}
            >
              <FormControl className="w-full">
                <SelectTrigger>
                  <SelectValue placeholder={t('plan.placeholder')} />
                </SelectTrigger>
              </FormControl>
              <SelectContent>
                {plans.map((plan) => (
                  <SelectItem key={plan.id} value={plan.id}>
                    {plan.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <FormMessage />
          </FormItem>
        )}
      />

      <FormField
        control={control}
        name="startDate"
        render={({ field }) => (
          <FormItem>
            <FormLabel>{t('startDate.label')}</FormLabel>
            <FormControl>
              <DatePicker
                value={field.value}
                name={field.name}
                ref={field.ref}
                onBlur={field.onBlur}
                onChange={(value) => {
                  field.onChange(value)
                  onStartDateChange(value)
                }}
              />
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      />

      <FormField
        control={control}
        name="endDate"
        render={({ field }) => (
          <FormItem>
            <FormLabel>{t('endDate.label')}</FormLabel>
            <FormControl>
              <DatePicker value={field.value} onChange={field.onChange} onBlur={field.onBlur} name={field.name} ref={field.ref} />
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      />

      <FormField
        control={control}
        name="price"
        render={({ field }) => (
          <FormItem>
            <FormLabel>{t('price.label')}</FormLabel>
            <FormControl>
              <Input
                type="number"
                min={0}
                step={1}
                {...field}
                onChange={(event) => field.onChange(event.target.valueAsNumber)}
              />
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      />
    </div>
  )
}
